/* eslint-disable */
import React, { useMemo } from 'react';

const FLAGGED_GRID = '1.4fr 0.8fr 1.2fr 1.4fr 1.2fr 0.6fr 1fr 1fr 0.8fr';

/**
 * FlaggedOnSite — escalation queue for devices flagged as missing during shift close.
 */
export default function FlaggedOnSite({ st, setSt, account, audit, isAdmin, isManager }) {
  const flaggedAllocs = useMemo(() =>
    (st.allocations || []).filter(a => a.status === 'flagged_missing' && !a.recoveredAt),
    [st.allocations]
  );

  const handleRecover = (allocId) => {
    const now = new Date().toISOString();
    const alloc = flaggedAllocs.find(a => a.id === allocId);
    if (!alloc) return;

    setSt(prev => ({
      ...prev,
      allocations: prev.allocations.map(a =>
        a.id === allocId ? { ...a, recoveredAt: now, status: 'returned' } : a
      ),
      devices: prev.devices.map(d =>
        d.id === alloc.deviceId ? { ...d, availability: 'available' } : d
      ),
    }));

    audit('flagged_recovered', { allocationId: allocId, deviceId: alloc.deviceId, recoveredBy: account?.username });
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

  const ageDays = (flaggedAt) => {
    if (!flaggedAt) return 0;
    return Math.floor((Date.now() - new Date(flaggedAt).getTime()) / 86400000);
  };

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-default)', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            Flagged: Still on site
            <span className="pill pill-danger" style={{ fontSize: 11 }}>
              {flaggedAllocs.length} device{flaggedAllocs.length !== 1 ? 's' : ''}
            </span>
          </h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Devices not returned when their shift was closed. These remain flagged until physically recovered.
          </div>
        </div>
      </div>

      {/* Warning banner */}
      {flaggedAllocs.length > 0 && (
        <div style={{ padding: '12px 16px', background: 'var(--warning-fill)', border: '1px solid var(--warning-border)', borderRadius: 'var(--radius-xl)', fontSize: 13, color: 'var(--warning-text)', marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 2 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <div>
            <strong style={{ color: 'var(--text-default)', display: 'block', marginBottom: 2 }}>These devices are physically unaccounted for</strong>
            A supervisor chose "Flag" during shift close. Once recovered, use "Mark recovered" to clear the flag and return the device to stock.
          </div>
        </div>
      )}

      {/* Table */}
      {flaggedAllocs.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, marginBottom: 10, display: 'inline-block' }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>No flagged devices</div>
          <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>All devices accounted for.</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginBottom: 6, paddingLeft: 2 }}>
            {flaggedAllocs.length} flagged device{flaggedAllocs.length !== 1 ? 's' : ''}
          </div>
          <div style={{ border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: FLAGGED_GRID, gap: 12, padding: '10px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
              {['Device', 'Role', 'Last Operative', 'Flagged From', 'Flagged At', 'Age', 'Flagged By', 'Location', ''].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', letterSpacing: 0.6, textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>
            {/* Rows */}
            {flaggedAllocs.map(alloc => {
              const device = (st.devices || []).find(d => d.id === alloc.deviceId);
              const shift = (st.shifts || []).find(s => s.id === alloc.shiftId);
              const loc = shift ? (st.locations || []).find(l => l.id === shift.locationId) : null;
              const age = ageDays(alloc.flaggedAt);
              const ageStyle = age > 14 ? { color: 'var(--danger-text)', fontWeight: 600 } : age > 7 ? { color: 'var(--warning-text)', fontWeight: 600 } : {};

              return (
                <div
                  key={alloc.id}
                  style={{
                    display: 'grid', gridTemplateColumns: FLAGGED_GRID, alignItems: 'center', gap: 12,
                    padding: '12px 16px', background: 'var(--surface-default)', borderBottom: '1px solid var(--border-default)',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-inset)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-default)'; }}
                >
                  {/* Device */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--danger-text)', flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: alloc.anonDeviceLabel ? 'monospace' : 'inherit' }}>{alloc.anonDeviceLabel || device?.serial || '?'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{device?.type || ''}</div>
                    </div>
                  </div>
                  {/* Role */}
                  <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>
                    {alloc.role || '—'}
                    {(st.assignmentRoles || []).find(r => r.name === alloc.role)?.isPlantDefault && <span style={{ color: 'var(--text-subtle)', marginLeft: 4, fontSize: 11 }}>Plant</span>}
                  </div>
                  {/* Last operative */}
                  <div style={{ fontSize: 12, color: alloc.operativeName ? 'var(--text-primary)' : 'var(--text-ghost)', fontStyle: alloc.operativeName ? 'normal' : 'italic' }}>
                    {alloc.operativeName || 'Unassigned plant'}
                  </div>
                  {/* Flagged from */}
                  <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{shift ? `Shift ${shift.shiftNumber} · ${shift.possessionRef}` : '—'}</div>
                  {/* Flagged at */}
                  <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{fmtDate(alloc.flaggedAt)} · {fmtTime(alloc.flaggedAt)}</div>
                  {/* Age */}
                  <div style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', ...ageStyle }}>{age} day{age !== 1 ? 's' : ''}</div>
                  {/* Flagged by */}
                  <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{alloc.flaggedByUserId || '—'}</div>
                  {/* Location */}
                  <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>{loc?.name || '—'}</div>
                  {/* Actions */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {(isAdmin || isManager) && (
                      <button onClick={() => handleRecover(alloc.id)} className="btn btn-ghost btn-sm" style={{ color: 'var(--success-text)', borderColor: 'var(--success-border)' }}>Mark recovered</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
